'use client';

import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, Variants } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import CountUp from 'react-countup';

interface HeroSectionProps {
  className?: string;
}

type Particle = {
  top: number;
  left: number;
  size: number;
  speed: number;
  delay: number;
  duration: number;
};

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [badgeIndex, setBadgeIndex] = useState(0);
  const containerRef = useRef<HTMLElement>(null);
  const [particles, setParticles] = useState<Particle[]>([]);
  // Update time every minute
  useEffect(() => {
  const now = new Date()
  setCurrentTime(now)

  const timer = setInterval(() => setCurrentTime(new Date()), 60000)
  return () => clearInterval(timer)
}, [])

  const getTimeBasedContent = () => {
    const hour = currentTime.getHours();
    if (hour >= 6 && hour < 12) {
      return {
        greeting: 'Good Morning',
        theme: 'dawn',
        videoSrc: 'https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4',
      };
    } else if (hour >= 12 && hour < 18) {
      return {
        greeting: 'Good Afternoon',
        theme: 'daylight',
        videoSrc: 'https://videos.pexels.com/video-files/3209828/3209828-uhd_2560_1440_25fps.mp4',
      };
    } else {
      return {
        greeting: 'Good Evening',
        theme: 'golden',
        videoSrc: 'https://videos.pexels.com/video-files/3571077/3571077-uhd_2560_1440_30fps.mp4',
      };
    }
  };

  const timeContent = getTimeBasedContent();

  // Parallax effect on mouse move
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll based opacity for background
  const { scrollY } = useScroll();
  const bgOpacity = useTransform(scrollY, [0, 300], [0.3, 0]);

  // Animation variants
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  const glowVariants: Variants = {
    animate: {
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.6, 0.3],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };
  useEffect(() => {
  const generated: Particle[] = Array.from({ length: 60 }, () => ({
  top: Math.random() * 100,
  left: Math.random() * 100,
  size: 2 + Math.random() * 4,
  speed: 1 + Math.random() * 3,
  delay: Math.random() * 5,
  duration: 5 + Math.random() * 10,
}));

  setParticles(generated);
}, []);
  return (
    <motion.section
  ref={containerRef}
  data-header="dark"
  className={`relative w-full min-h-[100dvh] text-white overflow-hidden ${className}`}
  initial="hidden"
  animate="visible"
  variants={containerVariants}
>
      {/* Video Background with Overlay */}
      <div className="absolute inset-0">
        <video
          key={timeContent.videoSrc}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="/assets/hero.mp4" type="video/mp4" />
        </video>
        {/* Primary brightness mask */}
        <div className="absolute inset-0 bg-black/30" />

        {/* Cinematic gradient mask */}

        {/* Scroll fade */}
        <motion.div className="absolute inset-0 bg-black" style={{ opacity: bgOpacity }} />
      </div>

      {/* Animated Gradient Orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-accent/30 blur-3xl"
        variants={glowVariants}
        animate="animate"
        style={{
          x: mousePosition.x * 2,
          y: mousePosition.y * 2,
        }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-brand-forest/40 blur-3xl"
        variants={glowVariants}
        animate="animate"
        style={{
          x: mousePosition.x * -1.5,
          y: mousePosition.y * -1.5,
        }}
      />

      {/* Particle Effect (CSS only) */}
      <div className="absolute inset-0 pointer-events-none">
        {particles.map((p, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full animate-float"
            style={{
              top: `${p.top}%`,
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex w-full h-full flex-col items-center justify-center mt-28 mb-28 px-4 text-center">
        <div className="flex w-full flex-col items-center justify-center gap-3 sm:gap-6 md:gap-10">
          {/* Badge with rotating text */}
          <motion.div variants={itemVariants}>
            <motion.span
              className="inline-block rounded-full bg-white/10 backdrop-blur-md px-6 py-2 text-xs sm:text-sm font-medium text-white border border-white/20 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                key={badgeIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="inline-block"
              >
                • {timeContent.greeting}, welcome to Grandville Holdings •
              </motion.span>
            </motion.span>
          </motion.div>

          {/* Headline with split animation */}
          <motion.h1
            variants={itemVariants}
            className="font-headline text-4xl sm:text-5xl lg:text-7xl font-bold mb-2 sm:mb-4 md:mb-6 leading-tight w-full"
          >
            <span className="inline-block overflow-hidden">
              {'Building'.split('').map((char, index) => (
                <motion.span
                  key={index}
                  className="inline-block"
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.1 + index * 0.03, duration: 0.5 }}
                >
                  {char}
                </motion.span>
              ))}
            </span>{' '}
            <span className="text-yellow-400 inline-block overflow-hidden">
              {'What Lasts'.split('').map((char, index) => (
                <motion.span
                  key={index}
                  className="inline-block"
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.1 + index * 0.03 + 0.5, duration: 0.5 }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>
              ))}
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="font-body text-base sm:text-lg lg:text-2xl text-gray-200 mb-2 sm:mb-4 md:mb-10 leading-relaxed w-full max-w-screen-xl"
          >
            Patient capital driving sustainable transformation across Africa through systematic
            methodology, authentic partnership, and generational wealth creation.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row justify-center gap-4 mb-6 md:mb-12 w-full max-w-screen-xl"
          >
            <Link
              href="/partnership-portal"
              className="group inline-flex items-center justify-center px-2 py-2 sm:py-4 bg-yellow-400 text-black font-body font-semibold text-base sm:text-lg rounded-md hover:bg-yellow-400/90 transition-all shadow-lg hover:shadow-accent/50"
            >
              <span>Explore Partnership Opportunities</span>
              <motion.span
                className="ml-2"
                animate={{ x: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
              >
                <Icon name="ArrowRightIcon" size={18} variant="outline" />
              </motion.span>
            </Link>

            <Link
              href="/the-transformation-agenda"
              className="group inline-flex items-center justify-center px-7 py-2 sm:py-4 bg-white/10 backdrop-blur-md text-white font-body font-semibold text-base sm:text-lg rounded-md border border-white/20 hover:bg-white/20 transition-all shadow-lg"
            >
              <span>View 20-Year Vision</span>
              <motion.span
                className="ml-2"
                whileHover={{ rotate: 15 }}
                transition={{ type: 'spring' }}
              >
                <Icon name="DocumentTextIcon" size={18} variant="outline" />
              </motion.span>
            </Link>
          </motion.div>

          {/* Stats with glass cards */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 w-full max-w-screen-xl"
          >
            {[
              {
                end: 2,
                suffix: 'Bn',
                label: 'Target Capital Deployment',
                icon: 'CurrencyDollarIcon',
              },
              { end: 10, suffix: ' Years', label: 'Phased Implementation', icon: 'ClockIcon' },
              { end: 55, suffix: 'K+', label: 'Projected Jobs Created', icon: 'UserGroupIcon' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="group bg-white/5 backdrop-blur-md p-2 sm:p-4 rounded-lg border border-white/10 shadow-xl hover:bg-white/10 transition-all"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="font-headline text-3xl lg:text-4xl font-semibold text-accent mb-2 flex items-center justify-center">
                  <span className="mr-1 opacity-70 group-hover:opacity-100 transition-opacity">
                    <Icon name={stat.icon} size={32} variant="outline" />
                  </span>
                  <CountUp end={stat.end} duration={2.5} />
                  {stat.suffix}
                </div>
                <div className="font-body text-sm text-gray-200">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center">
          <motion.div
            className="w-1 h-2 bg-white rounded-full mt-2"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          />
        </div>
      </motion.div>

      {/* Add floating animation keyframes via style tag */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(0) translateX(20px);
          }
          75% {
            transform: translateY(20px) translateX(10px);
          }
        }
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </motion.section>
  );
};

export default HeroSection;
